import { ref, watch, onMounted, onUnmounted, nextTick, type Ref } from 'vue'

export interface UseDropdownOptions {
  /** Селектор для элемента триггера (для проверки клика вне) */
  triggerSelector?: string
  /** Селектор для элемента dropdown (для проверки клика вне) */
  dropdownSelector?: string
  /** Приблизительная высота dropdown для расчета позиции */
  dropdownHeight?: number
  /** Отступ от триггера */
  offset?: number
  /** Минимальная высота элемента списка (для расчета максимального количества видимых элементов) */
  itemHeight?: number
  /** Минимальная высота dropdown */
  minHeight?: number
  /** Максимальная высота dropdown */
  maxHeight?: number
}

/**
 * Composable для управления dropdown: позиционирование, клики вне элемента, обработка событий
 */
export function useDropdown(
  isOpen: Ref<boolean>,
  triggerRef: Ref<HTMLElement | null>,
  options: UseDropdownOptions = {}
) {
  const {
    triggerSelector = '',
    dropdownSelector = '',
    dropdownHeight = 200,
    offset = 12,
    itemHeight = 40,
    minHeight = 100,
    maxHeight = 400,
  } = options

  const dropdownStyle = ref<Record<string, string>>({})

  /**
   * Обновляет позицию dropdown относительно триггера
   */
  const updateDropdownPosition = () => {
    if (!triggerRef.value) return

    const rect = (triggerRef.value.querySelector('button') ?? triggerRef.value).getBoundingClientRect()
    const triggerHeight = rect.height
    
    // Вычисляем доступное пространство с учетом высоты триггера и отступа
    const spaceBelow = window.innerHeight - rect.bottom - offset
    const spaceAbove = rect.top - offset

    // Вычисляем максимальную доступную высоту (вычитаем высоту триггера)
    const maxAvailableHeightBelow = spaceBelow
    const maxAvailableHeightAbove = spaceAbove
    
    // Уменьшаем высоту на половину высоты элемента, чтобы последний элемент был частично виден
    const peekHeight = itemHeight * 0.5
    
    // Выбираем направление открытия и вычисляем доступную высоту
    const openUpwards = spaceBelow < minHeight && spaceAbove > spaceBelow
    
    let adjustedHeight: number
    if (openUpwards) {
      adjustedHeight = maxAvailableHeightAbove - peekHeight
    } else {
      adjustedHeight = maxAvailableHeightBelow - peekHeight
    }
    
    // Ограничиваем высоту минимальным и максимальным значениями
    const calculatedMaxHeight = Math.max(
      minHeight,
      Math.min(adjustedHeight, maxHeight)
    )

    if (openUpwards) {
      // Открываем вверх: используем top (а не bottom), щоб уникнути стрибків при першому рендері
      dropdownStyle.value = {
        position: 'fixed',
        top: `${Math.max(8, rect.top - offset - calculatedMaxHeight)}px`,
        left: `${rect.left}px`,
        minWidth: '80px',
        maxHeight: `${calculatedMaxHeight}px`,
      }
    } else {
      // Открываем вниз
      dropdownStyle.value = {
        position: 'fixed',
        top: `${rect.bottom + offset}px`,
        left: `${rect.left}px`,
        minWidth: '80px',
        maxHeight: `${calculatedMaxHeight}px`,
      }
    }
  }

  /**
   * Обработчик клика вне элемента
   */
  const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as HTMLElement

    // Если указаны селекторы, используем их
    if (triggerSelector && dropdownSelector) {
      if (
        !target.closest(triggerSelector) &&
        !target.closest(dropdownSelector)
      ) {
        isOpen.value = false
      }
      return
    }

    // Иначе используем ref элемент и ищем dropdown по селектору или role
    if (triggerRef.value) {
      const triggerElement = triggerRef.value
      const dropdownElement = dropdownSelector
        ? document.querySelector(dropdownSelector)
        : document.querySelector('[role="listbox"]')
      
      if (
        !triggerElement.contains(target) &&
        !(dropdownElement && dropdownElement.contains(target))
      ) {
        isOpen.value = false
      }
    }
  }

  /**
   * Обновляет позицию при открытии
   */
  const onOpen = () => {
    nextTick(() => {
      updateDropdownPosition()
      requestAnimationFrame(() => updateDropdownPosition())
    })
  }

  // Подписываемся на изменения isOpen
  watch(isOpen, (open) => {
    if (open) {
      onOpen()
      window.addEventListener('scroll', updateDropdownPosition, true)
      window.addEventListener('resize', updateDropdownPosition)
    } else {
      window.removeEventListener('scroll', updateDropdownPosition, true)
      window.removeEventListener('resize', updateDropdownPosition)
    }
  })

  // Управление слушателями кликов
  onMounted(() => {
    if (process.client) {
      document.addEventListener('click', handleClickOutside)
    }
  })

  onUnmounted(() => {
    if (process.client) {
      document.removeEventListener('click', handleClickOutside)
      window.removeEventListener('scroll', updateDropdownPosition, true)
      window.removeEventListener('resize', updateDropdownPosition)
    }
  })

  return {
    dropdownStyle,
    updateDropdownPosition,
  }
}
